import { useState } from "react";
import clsx from "classnames";
import {
  clearNotificationGQL,
  Query,
  getUserNotificationsGQL,
  meGQL,
} from "@onmo/graphql/graphql";
import { useMutation, useQuery } from "@apollo/client";
import {
  TypographyVariant,
  TypographyColor,
} from "../../../constants/typography";
import { Dialog } from "../../core/Dialog/Dialog";
import { Typography } from "../../core/Typography/Typography";
import { Button } from "../../core/Button/Button";
import { Icon } from "../../core/Icon/Icon";
import { useAppSelector } from "../../hooks";
import { selectIsDesktopView } from "../../../store/slices/appState";
import { StaticImage } from "../../core/StaticImage/StaticImage";
import LogoIcon from "../../../assets/icons/logo_onmo+_3x.webp";
import Devices from "../../../assets/welcome/devices.webp";
import Devices2x from "../../../assets/welcome/devices@2x.webp";
import Devices3x from "../../../assets/welcome/devices@3x.webp";
import { isTV } from "../../../utils/platform";
import { FreeMonthReward } from "../FreeMonthReward";
import { isFounderNotifications } from "../../../models/user";
import { useStyles } from "./WelcomePopup.style";

type IWelcomePopupPopup = {
  onClose: () => void;
  isOpen: boolean;
};

export const WelcomePopup: React.FC<IWelcomePopupPopup> = (props) => {
  const classes = useStyles();
  const { isOpen, onClose } = props;
  const isDesktop = useAppSelector(selectIsDesktopView);
  const [clearNotification] = useMutation(clearNotificationGQL);
  const { data: dataMe } = useQuery<{
    me: Query["me"];
  }>(meGQL);
  const { data: dataNotifications } = useQuery(getUserNotificationsGQL, {
    variables: { product: "aaa", userId: dataMe?.me?.id },
    skip: !dataMe?.me?.id,
    fetchPolicy: "cache-and-network",
  });
  const { isBonus, isFounder } = isFounderNotifications(
    dataNotifications?.user?.notifications || [],
  );
  const [isShowReward, setIsShowReward] = useState(false);

  const onCloseWelcome = () => {
    onClose();
    clearNotification();
  };

  const onSubmit = () => {
    isBonus ? setIsShowReward(true) : onCloseWelcome();
  };

  const renderContent = () => {
    if (isShowReward) {
      return <FreeMonthReward onClose={onCloseWelcome} />;
    }
    return (
      <>
        {isFounder && (
          <div className={clsx(classes.accessLabel, !isTV && "linearBg")}>
            <Icon name="icon-light" />
            <Typography
              variant={TypographyVariant.BodyB6Medium}
              className={classes.accessText}
            >
              FOUNDERS’ ACCESS
            </Typography>
          </div>
        )}
        <Typography className={classes.title}>Welcome to</Typography>
        <StaticImage src={LogoIcon} className={classes.logo} />
        {!isDesktop && (
          <StaticImage
            src={Devices}
            src2x={Devices2x}
            src3x={Devices3x}
            className={classes.devices}
          />
        )}
        <Typography
          variant={
            isDesktop
              ? TypographyVariant.HeadingH5Bold
              : TypographyVariant.BodyB4Bold
          }
          className={classes.subTitle}
        >
          Premium gaming on all your screens
        </Typography>
        <div className={classes.card}>
          <Icon name="icon-ai" className={classes.aiIcon} />
          <div>
            <Typography
              variant={TypographyVariant.BodyB5Bold}
              className={classes.installText}
            >
              Install & Play access included
            </Typography>
            <Typography
              variant={TypographyVariant.BodyB5Medium}
              className={classes.desc}
            >
              A founder’s privilege — yours from day one
            </Typography>
          </div>
        </div>
        <Typography
          variant={TypographyVariant.BodyB6Regular}
          color={TypographyColor.Gray}
        >
          We’re still polishing a few things — your feedback shapes what comes
          next.
        </Typography>
        <Button
          className={clsx(classes.goBtn, !isTV && "linearBg")}
          fullWidth={!isDesktop}
          onClick={onSubmit}
          data-gamepad="default-selected"
        >
          <Icon name="icon-light-fill" /> Let’s Go
        </Button>
      </>
    );
  };

  return (
    <Dialog
      open={isOpen}
      rootClassName={clsx(
        classes.root,
        isShowReward && classes.rootReward,
        isDesktop && "isDesktop",
      )}
    >
      <div className={classes.contentDialog}>{renderContent()}</div>
    </Dialog>
  );
};
